import { useEffect, useState, useCallback } from "react";
import API from "../../../services/api";
import "./SchoolPages.css";

const PAGE_CONFIG = {
  about:   { label: "About School",       icon: "🏫" },
  terms:   { label: "Terms & Conditions", icon: "📜" },
  privacy: { label: "Privacy Policy",     icon: "🔒" },
  contact: { label: "Contact Us",         icon: "📞" },
};

export default function SchoolPages() {
  const school  = JSON.parse(localStorage.getItem("schoolAdmin"));
  const schoolId = school?.school?.id;

  const [pages,    setPages]    = useState({});
  const [pageType, setPageType] = useState("about");
  const [title,    setTitle]    = useState("");
  const [content,  setContent]  = useState("");
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState(null);   // { msg, ok }

  /* ── load all pages ── */
  const loadPages = useCallback(async () => {
    try {
      const res = await API.post("/school/pages/", {}, {
        headers: { "X-SCHOOL-ID": schoolId },
      });
      setPages(res.data.pages || {});
    } catch (err) { console.error(err); }
  }, [schoolId]);

  useEffect(() => { loadPages(); }, [loadPages]);

  /* ── populate editor when pages or active type changes ── */
  useEffect(() => {
    if (pages[pageType]) {
      setTitle(pages[pageType].title   || "");
      setContent(pages[pageType].content || "");
    } else {
      setTitle(""); setContent("");
    }
  }, [pageType, pages]);

  /* ── show toast ── */
  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  /* ── save ── */
  const savePage = async () => {
    if (!title.trim() || !content.trim())
      return alert("Title and content are required");
    try {
      setSaving(true);
      await API.post(
        "/school-admin/pages/save/",
        { page_type: pageType, title, content },
        { headers: { "X-SCHOOL-ID": schoolId } }
      );
      await loadPages();
      showToast("✅ Page saved successfully");
    } catch (err) {
      console.error(err);
      showToast("❌ " + (err.response?.data?.error || "Save failed"), false);
    } finally { setSaving(false); }
  };

  const publishedCount = Object.keys(PAGE_CONFIG).filter(k => pages[k]).length;
  const active = PAGE_CONFIG[pageType];

  return (
    <div className="sp-page">

      {/* ══ HERO ══ */}
      <div className="sp-hero">
        <div className="sp-hero-left">
          <div className="sp-hero-icon">📄</div>
          <div>
            <h2 className="sp-hero-title">School Pages CMS</h2>
            <p className="sp-hero-sub">
              {publishedCount} of {Object.keys(PAGE_CONFIG).length} pages published
            </p>
          </div>
        </div>
      </div>

      {/* ══ LAYOUT ══ */}
      <div className="sp-layout">

        {/* LEFT — page type list */}
        <div className="sp-sidebar">
          <p className="sp-sidebar-title">Pages</p>
          {Object.entries(PAGE_CONFIG).map(([key, cfg]) => (
            <div
              key={key}
              className={`sp-type-card ${pageType === key ? "active" : ""}`}
              onClick={() => setPageType(key)}
            >
              <span className="sp-type-icon">{cfg.icon}</span>
              <div className="sp-type-info">
                <div className="sp-type-name">{cfg.label}</div>
                <div className={`sp-type-status ${pages[key] ? "published" : "empty"}`}>
                  {pages[key] ? "✓ Published" : "⚪ Not added"}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT — editor */}
        <div className="sp-editor-card">
          <div className="sp-editor-head">
            <span className="sp-editor-head-icon">{active.icon}</span>
            <div>
              <div className="sp-editor-head-title">{active.label}</div>
              <div className="sp-editor-head-sub">
                {pages[pageType] ? "Editing existing page" : "Create new page"}
              </div>
            </div>
          </div>

          <div className="sp-editor-body">
            <div className="sp-field">
              <label>Title *</label>
              <input
                placeholder={`e.g. ${active.label}`}
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
            </div>

            <div className="sp-field">
              <label>Content *</label>
              <textarea
                placeholder="Write the full page content here…"
                value={content}
                onChange={e => setContent(e.target.value)}
              />
              <div className="sp-char-count">{content.length} characters</div>
            </div>

            <button className="sp-btn-save" onClick={savePage} disabled={saving}>
              {saving
                ? <><div className="sp-spinner"></div> Saving…</>
                : `💾 Save ${active.label}`}
            </button>
          </div>
        </div>

      </div>

      {/* ══ TOAST ══ */}
      {toast && (
        <div className="sp-toast">{toast.msg}</div>
      )}

    </div>
  );
}
